import { useCallback, useMemo } from 'react';

import type { Group } from '@bugbug/core/types/groups';
import type { Step } from '@bugbug/core/types/steps';
import type { Test } from '@bugbug/core/types/tests';
import { StepsActions } from '~/modules/steps/steps.redux';
import { selectCurrentStepId } from '~/modules/steps/steps.selectors';
import { useAppDispatch, useAppSelector } from '~/modules/store';
import { useCleanupAfterTestPartialsDelete } from '~/modules/tests/hooks/useCleanupAfterTestPartialsDelete/useCleanupAfterTestPartialsDelete';
import { useCheckedSteps } from '~/modules/uiState/hooks/useCheckedSteps';
import analytics from '~/services/analytics';
import { ACTIONS } from '~/services/toasts/internalServerError';

import { showStepsError } from '../../../tests/tests.utils';
import { stepsApi } from '../../steps.api';

export const useDeleteStep = () => {
  const dispatch = useAppDispatch();
  const [deleteStep, deleteStepState] = stepsApi.useDeleteStepMutation();
  const currentStepId = useAppSelector(selectCurrentStepId);
  const cleanUpAfterDelete = useCleanupAfterTestPartialsDelete();
  const { setCheckedStepInGroup } = useCheckedSteps();

  const handleDeleteStep = useCallback(
    async ({
      id,
      testId,
      groupId,
    }: {
      id: Step['id'];
      testId: Test['id'];
      groupId: Group['id'];
    }) => {
      analytics.trackEvent('step_deleted');
      const result = deleteStep({ id, testId }).unwrap();

      result
        .then(() => {
          if (currentStepId === id) {
            dispatch(StepsActions.resetCurrentStep());
          }

          setCheckedStepInGroup({ step: { id, groupId }, checked: false });
          cleanUpAfterDelete({
            stepsIds: [id],
            groupsIds: [],
          });
        })
        .catch((error) => {
          showStepsError(ACTIONS.DELETE, error);
        });

      return result;
    },
    [cleanUpAfterDelete, currentStepId, deleteStep, dispatch, setCheckedStepInGroup],
  );

  return useMemo(
    () => [handleDeleteStep, deleteStepState] as const,
    [handleDeleteStep, deleteStepState],
  );
};
